import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { FormatCurrencyPipe } from '@app/shared/pipes/format-currency.pipe';
import { SpinnerService } from '@app/shared/spinner.service';
import { parseTz } from '@app/shared/time';
import { Station, Store } from '@app/shared/_models/common';
import { ApiRefusal, Bank, BanksList, ChequePortfolio, ChequeToRemit, NO_LINKED_BANK_MSG, bankChoices, defaultBank, lostCause, lostReply, refusalText, sumCents } from '../reglement.models';
import { ChequeRemittanceOutcome, ReglementService } from '../reglement.service';

// Filtres de la lecture affichée par l'onglet (jours métier inclus) et droits relus en base par lui.
export interface ChequeRemittanceDialogData {
	from: string;
	to: string;
	store: number | null;
	station: number | null;
	stores: Store[];
	stations: Station[];
	canRemit: boolean;
}

export const NO_LINKED_BANK_ALL_MSG = 'Aucune banque liée aux magasins (lien Banque à poser dans PostgreSQL)';
export const REMIT_RESERVED_MSG = 'Remise réservée aux administrateurs et à l\'habilitation Coffres-forts.';

@Component({
	selector: 'app-cheque-remittance-dialog',
	standalone: true,
	templateUrl: './cheque-remittance-dialog.component.html',
	styleUrls: ['./cheque-remittance-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormsModule, MatDialogModule, MatButtonModule, MatCheckboxModule, MatIconModule, MatTooltipModule, FormatCurrencyPipe],
})
export class ChequeRemittanceDialogComponent implements OnInit {

	private reglementService = inject(ReglementService);
	private spin = inject(SpinnerService);
	private destroyRef = inject(DestroyRef);
	public dialogRef = inject(MatDialogRef<ChequeRemittanceDialogComponent, ChequeRemittanceOutcome>);
	public data: ChequeRemittanceDialogData = inject(MAT_DIALOG_DATA);
	readonly reservedMsg = REMIT_RESERVED_MSG;

	public portfolio: ChequePortfolio | null = null;
	public loadError = '';
	// null tant que la liste n'est pas lue (ou illisible) : « aucune banque liée » ne se dit que d'une liste lue et vide.
	public banks: Bank[] | null = null;
	public defaultBankId: number | null = null;
	public banksError = '';
	public bank: number | null = null;
	// Chèques écartés par l'utilisateur : tout le reste du portefeuille part en remise ; survit à un rechargement, même raté.
	private excluded = new Set<number>();
	public sending = false;
	public sendError = '';
	// 409 : la liste a changé côté serveur (chèque remis, annulé ou extourné) ; seul un rechargement la remet d'accord.
	public conflict = false;
	// Réponse perdue : remise peut-être enregistrée ; comme le 409, seul un rechargement dit ce qui reste à remettre.
	public lost = false;
	// Survit au rechargement : l'onglet se relit à la fermeture, sa liste des remises a pu être lue avant le commit.
	private uncertain = false;
	public done: ChequeRemittanceOutcome | null = null;
	public printError = '';

	ngOnInit(): void {
		this.reglementService.getBanks$().pipe(
			map((list): { list: BanksList | null; error: string } => ({ list, error: '' })),
			catchError((e) => of({ list: null, error: refusalText(e) })),
			takeUntilDestroyed(this.destroyRef),
		).subscribe(({ list, error }) => {
			this.banksError = error;
			this.banks = list ? bankChoices(list.stores, list.banks, this.data.store) : null;
			this.defaultBankId = list ? defaultBank(list.stores, this.data.store) : null;
			this.bank = this.defaultBankId;
		});
		this.loadPortfolio();
	}

	loadPortfolio(): void {
		this.loadError = '';
		this.sendError = '';
		this.conflict = false;
		this.lost = false;
		const d = this.data;
		this.reglementService.getChequePortfolio$({ from: d.from, to: d.to, store: d.store, station: d.station })
			.pipe(this.spin.block('Lecture des chèques à remettre…'), takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (p) => this.portfolio = { ...p, cheques: p?.cheques ?? [] },
				error: (e) => { this.portfolio = null; this.loadError = refusalText(e); },
			});
	}

	get cheques(): ChequeToRemit[] {
		return this.portfolio?.cheques ?? [];
	}

	isChecked(id: number): boolean {
		return !this.excluded.has(id);
	}

	toggle(id: number, on: boolean): void {
		if (on) this.excluded.delete(id);
		else this.excluded.add(id);
	}

	private get checkedCheques(): ChequeToRemit[] {
		return this.cheques.filter((c) => !this.excluded.has(c.id));
	}

	get allChecked(): boolean {
		return this.cheques.length > 0 && this.checkedCheques.length === this.cheques.length;
	}

	get someChecked(): boolean {
		return this.checkedCheques.length > 0 && !this.allChecked;
	}

	toggleAll(on: boolean): void {
		this.cheques.forEach((c) => this.toggle(c.id, on));
	}

	// Ordre du portefeuille : le serveur compare des ensembles, mais une liste stable se relit mieux dans les journaux.
	get checkedIds(): number[] {
		return this.checkedCheques.map((c) => c.id);
	}

	get checkedTotal(): number {
		return sumCents(this.checkedCheques.map((c) => c.amount));
	}

	get noBankMsg(): string {
		return this.data.store === null ? NO_LINKED_BANK_ALL_MSG : NO_LINKED_BANK_MSG;
	}

	bankLabel(b: Bank): string {
		return (b.name ?? 'Banque ' + b.id) + (b.id === this.defaultBankId ? ' (par défaut)' : '');
	}

	get canValidate(): boolean {
		return this.data.canRemit && !this.done && !this.sending && !this.conflict && !this.lost && this.checkedIds.length > 0
			&& this.bank !== null && !!this.banks?.some((b) => b.id === this.bank);
	}

	// Appelé dans le clic : le service y ouvre la fenêtre d'impression avant le POST, puis la remplit du bordereau de la remise.
	validate(): void {
		if (!this.canValidate || this.spin.isLoading) return;
		this.sending = true;
		this.sendError = '';
		this.dialogRef.disableClose = true;	// fermer pendant l'envoi cacherait une remise peut-être enregistrée
		this.reglementService.remitChequesAndPrint$(this.bank!, this.checkedIds)
			.pipe(this.spin.block('Enregistrement de la remise…'), finalize(() => { this.sending = false; this.dialogRef.disableClose = this.uncertain; }), takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (r) => { this.done = r; this.printError = r.printError; },
				error: (e) => {
					this.lost = lostReply(e);
					this.uncertain ||= this.lost;
					this.conflict = e instanceof ApiRefusal && e.code === 409;
					this.sendError = this.lost
						? `Réponse perdue (${lostCause(e)}) : la remise a peut-être été enregistrée. Rechargez (la connexion revenue) : un chèque remis n'est plus à remettre, et la remise figure dans la liste de l'onglet.`
						: refusalText(e);
				},
			});
	}

	print(): void {
		if (!this.done) return;
		this.printError = '';
		this.reglementService.printChequeRemittance$(this.done.data.remittance).pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({ error: (e) => this.printError = refusalText(e) });
	}

	// Jamais pendant l'envoi, en doublure du [disabled] des boutons : fermer détruirait la boîte et couperait le POST, remise peut-être enregistrée.
	close(): void {
		if (this.sending) return;
		if (this.uncertain) this.reglementService.markUncertain('remittance');
		this.dialogRef.close(this.done ?? undefined);
	}

	get windowLabel(): string {
		const a = parseTz(this.data.from)?.format('DD/MM/YYYY') ?? this.data.from;
		const b = parseTz(this.data.to)?.format('DD/MM/YYYY') ?? this.data.to;
		return this.data.from === this.data.to ? 'le ' + a : `du ${a} au ${b}`;
	}

	get scopeLabel(): string {
		const store = this.data.store === null ? 'tous les magasins' : (this.data.stores.find((s) => s.id === this.data.store)?.name ?? 'magasin ' + this.data.store);
		const station = this.data.station === null ? '' : ' · ' + (this.data.stations.find((s) => s.id === this.data.station)?.name ?? 'poste ' + this.data.station);
		return `Chèques à remettre ${this.windowLabel} · ${store}${station}`;
	}

	stationName(c: ChequeToRemit): string {
		return this.data.stations.find((s) => s.id === c.station)?.name ?? 'Poste ' + c.station;
	}

	day(dat: string): string {
		return parseTz(dat)?.format('DD/MM/YYYY') ?? '';
	}
}
